import produce from "immer";
import {
  filter,
  find,
  flatten,
  get,
  head,
  keyBy,
  map,
  sumBy,
  values
} from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import AddWalletSidebar from "../../pages/Workspace/WalletsData/AddWalletSidebar";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Hotspot, Wallet, WalletVerification } from "../../utils/types";
import { useHntMarket } from "../../utils/useHntMarket";
import usePrevious from "../../utils/usePrevious";
import { useLabels } from "../labels";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { useStats } from "../stats";
import { useToast } from "../toast";
import { useWorkspaces } from "../workspaces";

interface WalletsMap {
  [walletId: string]: Wallet;
}

type ContextValue = {
  wallets: WalletsMap;
  verifiedWalletsCount: number;
  addWallet: (
    walletAddress: string,
    options?: { tracked: boolean }
  ) => Promise<ApiResponse<Wallet> | undefined>;
  trackWallet: (
    walletAddress: string,
    tracked: boolean
  ) => Promise<ApiResponse<Wallet> | undefined>;
  verifyWallet: (
    wallet: string,
    amount: number
  ) => Promise<ApiResponse<WalletVerification>>;
  removeWallet: (wallet: Wallet) => Promise<ApiResponse<Wallet> | undefined>;
  fetchWallets: () => Promise<{}>;
  removeAllWallets: () => Promise<ApiResponse<[]> | undefined>;
  getActiveWalletVerification: (
    walletAddress: string
  ) => WalletVerification | undefined;
  toggleAddWalletSidebar: (contextualHotspot?: Hotspot | undefined) => void;
  syncHotspots: (wallet: Wallet) => Promise<ApiResponse<Wallet> | undefined>;
};

const WalletsContext = React.createContext<ContextValue | undefined>(undefined);

interface Props {}

export const WalletsProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const { currentWorkspaceId, setWorkspaces, currentWorkspaceIsLocked } =
    useWorkspaces();
  const { addLoader, removeLoader } = useLoading();
  const { stats } = useStats();
  const { fetchLabels } = useLabels();
  const [wallets, setWallets] = useState<WalletsMap>({});
  const { addToast } = useToast();
  const { displayInCurrency } = useHntMarket();
  const [openAddWalletSidebar, setOpenAddWalletSidebar] = useState(false);
  const [walletSidebarHotspot, setWalletSidebarHotspot] = useState<
    Hotspot | undefined
  >();

  const fetchWallets = useCallback(async () => {
    if (!currentWorkspaceId) return {};

    addLoader(LoadingKey.workspaceWallets);

    const response = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/wallets`
    );

    const apiResponse: ApiResponse<Wallet[]> = await response.json();

    let newWallets = {};

    if (apiResponse.status) {
      newWallets = keyBy(apiResponse.data, "uuid");

      setWallets(newWallets);
    }

    removeLoader(LoadingKey.workspaceWallets);

    return newWallets;
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setWallets({});
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  useEffect(() => {
    if (currentWorkspaceIsLocked) return;

    fetchWallets();
  }, [currentWorkspaceIsLocked, fetchWallets]);

  const prevBlocks = usePrevious(stats?.blocks);

  useEffect(() => {
    if (prevBlocks && stats?.blocks && stats.blocks > prevBlocks) {
      fetchWallets();
    }
  }, [fetchWallets, prevBlocks, stats?.blocks]);

  const addWallet = useCallback(
    async (
      walletAddress: string,
      { tracked }: { tracked: boolean } | undefined = { tracked: true }
    ) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/wallets`,
        {
          method: "POST",
          body: JSON.stringify({ address: walletAddress, tracked })
        }
      );

      const apiResponse: ApiResponse<Wallet> = await response.json();

      if (apiResponse.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.wallet += 1;
          })
        );

        await Promise.all([fetchWallets(), fetchLabels()]);
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchLabels, fetchWallets, setWorkspaces]
  );

  const trackWallet = useCallback(
    async (walletAddress: string, tracked: boolean) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/wallet/${walletAddress}/tracked`,
        {
          method: "PUT",
          body: JSON.stringify({ tracked })
        }
      );

      const apiResponse: ApiResponse<Wallet> = await response.json();

      if (apiResponse.status) {
        await fetchWallets();
        addToast({
          type: "info",
          message: `The wallet has been ${tracked ? "tracked" : "untracked"}`
        });
      } else {
        addToast({
          type: "error",
          message: "Failed to track wallet"
        });
      }

      return apiResponse;
    },
    [addToast, currentWorkspaceId, fetchWallets]
  );

  const verifyWallet = useCallback(
    async (walletUuid: string, amount: number) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/wallet/${walletUuid}/verify`,
        {
          method: "POST",
          body: JSON.stringify({ amount })
        }
      );

      const apiResponse: ApiResponse<WalletVerification> =
        await response.json();

      await fetchWallets();

      return apiResponse;
    },
    [currentWorkspaceId, fetchWallets]
  );

  const removeWallet = useCallback(
    async (wallet: Wallet) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/wallets/${wallet.uuid}`,
        {
          method: "DELETE"
        }
      );

      const apiResponse: ApiResponse<Wallet> = await response.json();

      if (apiResponse.status) {
        await fetchWallets();

        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.wallet = values(wallets).length;
          })
        );
      } else {
        addToast({
          type: "error",
          message:
            "Failed to remove wallet, make sure you don't have any wallet hotspot in use"
        });
      }

      return apiResponse;
    },
    [addToast, currentWorkspaceId, fetchWallets, setWorkspaces, wallets]
  );

  const removeAllWallets = useCallback(async () => {
    if (!currentWorkspaceId) return;

    const response = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/bulk/wallets/remove`,
      {
        method: "DELETE"
      }
    );

    const apiResponse: ApiResponse<[]> = await response.json();

    if (apiResponse.status) {
      await fetchWallets();

      setWorkspaces((prevState) =>
        produce(prevState, (draft) => {
          if (!draft[currentWorkspaceId].counts) return;

          draft[currentWorkspaceId].counts.wallet = 0;
        })
      );
    } else {
      addToast({
        type: "error",
        message:
          "Failed to remove all wallets, make sure you don't have any wallet hotspot in use"
      });
    }

    return apiResponse;
  }, [addToast, currentWorkspaceId, fetchWallets, setWorkspaces]);

  const prevWallets = usePrevious(wallets);

  useEffect(() => {
    if (!currentWorkspaceId || !prevWorkspaceId) return;

    const prevBalance = sumBy(
      filter(values(prevWallets), "tracked"),
      "balance"
    );
    const currentBalance = sumBy(filter(values(wallets), "tracked"), "balance");

    if (
      prevBalance &&
      currentBalance &&
      currentBalance > prevBalance &&
      prevWorkspaceId === currentWorkspaceId
    ) {
      const addedRewards = currentBalance - prevBalance;

      addToast({
        type: "info",
        title: "New rewards 🤑",
        message: `You received ${displayInCurrency(addedRewards, {
          withHntSuffix: true,
          needsDivision: true
        })}!`
      });
    }
  }, [
    addToast,
    displayInCurrency,
    currentWorkspaceId,
    prevWorkspaceId,
    prevWallets,
    wallets
  ]);

  const getActiveWalletVerification = useCallback(
    (walletAddress: string) => {
      const currentWallet = find(values(wallets), { address: walletAddress });

      const walletVerifications = get(currentWallet, "verifications", []);

      const activeWalletVerifications = walletVerifications.filter(
        ({ expires_at, status }) =>
          status === "pending" &&
          moment(expires_at).utc().isAfter(moment().utc())
      );

      return head(activeWalletVerifications);
    },
    [wallets]
  );

  const verifiedWalletsCount = useMemo(() => {
    const walletVerifications = flatten(map(values(wallets), "verifications"));
    return filter(walletVerifications, { status: "completed" }).length;
  }, [wallets]);

  const toggleAddWalletSidebar = useCallback(
    (contextualHotspot?: Hotspot | undefined) => {
      if (openAddWalletSidebar) {
        setWalletSidebarHotspot(undefined);
        setOpenAddWalletSidebar(false);
        return;
      }

      setWalletSidebarHotspot(contextualHotspot);
      setOpenAddWalletSidebar(true);
    },
    [openAddWalletSidebar]
  );

  const syncHotspots = useCallback(
    async (wallet: Wallet) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/wallet/${wallet.uuid}/sync`
      );

      const apiResponse: ApiResponse<Wallet> = await response.json();

      if (apiResponse.status) {
        await fetchWallets();
        addToast({
          type: "info",
          message: "Wallet hotspots are being synced"
        });
      } else {
        addToast({
          type: "error",
          message: "Failed to sync wallet hotspots"
        });
      }

      return apiResponse;
    },
    [addToast, currentWorkspaceId, fetchWallets]
  );

  const value = useMemo(
    () => ({
      wallets,
      verifiedWalletsCount,
      fetchWallets,
      addWallet,
      verifyWallet,
      removeWallet,
      getActiveWalletVerification,
      toggleAddWalletSidebar,
      removeAllWallets,
      syncHotspots,
      trackWallet
    }),
    [
      wallets,
      verifiedWalletsCount,
      fetchWallets,
      addWallet,
      verifyWallet,
      removeWallet,
      getActiveWalletVerification,
      toggleAddWalletSidebar,
      removeAllWallets,
      syncHotspots,
      trackWallet
    ]
  );

  return (
    <WalletsContext.Provider value={value} {...otherProps}>
      <AddWalletSidebar
        open={openAddWalletSidebar}
        toggle={toggleAddWalletSidebar}
        walletAddress={walletSidebarHotspot?.owner}
        hotspot={walletSidebarHotspot}
      />
      {children}
    </WalletsContext.Provider>
  );
};

export const useWallets = (): ContextValue => {
  const context = useContext(WalletsContext);
  if (context === undefined) {
    throw new Error("useWallets must be used within an WalletsProvider");
  }
  return context;
};
